import { Grid, useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import ModalInfoCertification from 'front-office/components/shared/info-certificate';
import ModalCertificate from 'front-office/components/historical/request-certificate-modal';
import ModalPrintSuccess from 'front-office/components/shared/request-print';
import { Fragment, useCallback, useMemo, useState } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import * as yup from 'yup';
import { HistoricalFormVM } from 'front-office/view-models/historical/historical-form';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { __CERTIFICATION_API_URL__ } from 'shell/constants/environment';
import { transformRequestHistoricalData } from 'front-office/utils/transform-request-historical';
import { HttpResponse } from 'shell/models/http-response';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
// import { openPdf } from 'shell/utils/open-pdf';
import { HistoricalCrumbs } from 'front-office/constants/historical-crumbs';
import HistoricalForm from 'front-office/components/historical';
import HistoricalPageButtons from 'front-office/components/shared/buttons-certificate';
import { ResponseCertificateHistorical } from 'shell/models/response-certificate-historical';
import { transformResponseRequestCertificateHistorical } from 'front-office/utils/transform-response-certificate-historical';
import { CommonRequestCertificateInterface } from 'front-office/view-models/common-certificate-form';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import { AccordionAccountholderFormVM } from 'front-office/view-models/certificate/accountholder-form';
import { AccordionApplicantFormVM } from 'front-office/view-models/certificate/applicant-form';
import { DateTime } from 'luxon';
import { ObjectShape } from 'yup/lib/object';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { DocumentRadioCertificate } from 'front-office/constants/document-radio-certificate';
import PmiTitle from 'shell/components/shared/title';

const createSchema = (open: boolean) => {
  let schema: ObjectShape = {};

  if (open) {
    schema = {
      certifiableApplicant: yup.boolean(),
      certifiableAccountholder: yup.boolean(),
      certificateInternOnly: yup.boolean(),
      dateForm: yup.date().nullable().typeError('Data non valida'),
      applicant: yup.object().when('certificateInternOnly', {
        is: false,
        then: yup.object().shape({
          // fiscalCode: yup.string().required('Campo obbligatorio'),
          withoutSurname: yup.boolean(),
          withoutName: yup.boolean(),
          surname: yup.string().when('withoutSurname', {
            is: false || undefined,
            then: yup.string().required('Campo obbligatorio'),
            otherwise: yup.string(),
          }),
          name: yup.string().when('withoutName', {
            is: false || undefined,
            then: yup.string().required('Campo obbligatorio'),
            otherwise: yup.string(),
          }),
          gender: yup.string().required('Campo obbligatorio'),
        }),
        otherwise: yup.object().shape({}),
      }),
      generality: yup.object().when('certificateInternOnly', {
        is: false,
        then: yup.object().shape({
          searchApplicant: yup.boolean().required('Effettuare ricerca'),
          numberDocument: yup.string().required('Campo obbligatorio'),
          birthDate: yup
            .date()
            .nullable()
            .required('Campo obbligatorio')
            .typeError('Data non valida'),
          isForeign: yup.boolean(),
          foreignStateBirth: yup
            .object()
            .shape({})
            .nullable()
            .when('isForeign', {
              is: true,
              then: yup.object().shape({}).nullable().required(),
              otherwise: yup.object().shape({}).nullable(),
            }),
          foreignMunicipality: yup
            .object()
            .shape({})
            .nullable()
            .when('isForeign', {
              is: true,
              then: yup.object().shape({}).nullable().required(),
              otherwise: yup.object().shape({}).nullable(),
            }),
          municipalityOfBirth: yup
            .object()
            .shape({})
            .nullable()
            .when('isForeign', {
              is: true,
              otherwise: yup.object().shape({}).nullable().required(),
              then: yup.object().shape({}).nullable(),
            }),
          dateReleaseDocument: yup
            .date()
            .nullable()
            .required('Campo obbligatorio')
            .typeError('Data non valida'),
          isForeignDocument: yup.string(),
          municipalityDocument: yup
            .object()
            .nullable()
            .when('isForeignDocument', {
              is: DocumentRadioCertificate.IdentityCard,
              otherwise: yup.object().nullable().notRequired(),
              then: yup.object().required('Campo obbligatorio'),
            }),
        }),
        otherwise: yup.object().shape({}),
      }),
      accountholder: yup
        .object()
        .shape({})
        .when('certificateInternOnly', {
          is: true,
          then: yup.object().shape({}).required(),
        })
        .test('accountHolder', 'Campo obbligatorio', (values) => {
          const valuesCasted = values as unknown as AccordionAccountholderFormVM;

          // same as applicant
          if (valuesCasted?.sameApplicant) {
            return true;
          }

          // fiscal code required
          if (!valuesCasted?.account?.applicant?.fiscalCode) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              'accountholder.account.applicant.fiscalCode'
            );
          }

          // search required
          if (!valuesCasted?.searchAccountholder) {
            return new yup.ValidationError(
              'Effettuare ricerca',
              undefined,
              'accountholder.searchAccountholder'
            );
          }

          return true;
        }),
    };
  } else {
    schema = {
      certifiableApplicant: yup.boolean(),
      certifiableAccountholder: yup.boolean(),
      certificateInternOnly: yup.boolean(),
      applicant: yup.object().when('certificateInternOnly', {
        is: false,
        then: yup.object().shape({
          // fiscalCode: yup.string().required('Campo obbligatorio'),
          withoutSurname: yup.boolean(),
          withoutName: yup.boolean(),
          surname: yup.string().when('withoutSurname', {
            is: false || undefined,
            then: yup.string().required('Campo obbligatorio'),
            otherwise: yup.string(),
          }),
          name: yup.string().when('withoutName', {
            is: false || undefined,
            then: yup.string().required('Campo obbligatorio'),
            otherwise: yup.string(),
          }),
          gender: yup.string().required('Campo obbligatorio'),
        }),
        otherwise: yup.object().shape({}),
      }),
      generality: yup.object().when('certificateInternOnly', {
        is: false,
        then: yup.object().shape({
          searchApplicant: yup.boolean().required('Effettuare ricerca'),
          numberDocument: yup.string().required('Campo obbligatorio'),
          birthDate: yup
            .date()
            .nullable()
            .required('Campo obbligatorio')
            .typeError('Data non valida'),
          isForeign: yup.boolean(),
          foreignStateBirth: yup
            .object()
            .shape({})
            .nullable()
            .when('isForeign', {
              is: true,
              then: yup.object().shape({}).nullable().required(),
              otherwise: yup.object().shape({}).nullable(),
            }),
          foreignMunicipality: yup
            .object()
            .shape({})
            .nullable()
            .when('isForeign', {
              is: true,
              then: yup.object().shape({}).nullable().required(),
              otherwise: yup.object().shape({}).nullable(),
            }),
          municipalityOfBirth: yup
            .object()
            .shape({})
            .nullable()
            .when('isForeign', {
              is: true,
              otherwise: yup.object().shape({}).nullable().required(),
              then: yup.object().shape({}).nullable(),
            }),
          dateReleaseDocument: yup
            .date()
            .nullable()
            .required('Campo obbligatorio')
            .typeError('Data non valida'),
          isForeignDocument: yup.string(),
          municipalityDocument: yup
            .object()
            .nullable()
            .when('isForeignDocument', {
              is: DocumentRadioCertificate.IdentityCard,
              otherwise: yup.object().nullable().notRequired(),
              then: yup.object().required('Campo obbligatorio'),
            }),
        }),
        otherwise: yup.object().shape({}),
      }),
      accountholder: yup
        .object()
        .shape({})
        .when('certificateInternOnly', {
          is: true,
          then: yup.object().shape({}).required(),
        })
        .test('accountHolder', 'Campo obbligatorio', (values) => {
          const valuesCasted = values as unknown as AccordionAccountholderFormVM;

          // same as applicant
          if (valuesCasted?.sameApplicant) {
            return true;
          }

          // fiscal code required
          if (!valuesCasted?.account?.applicant?.fiscalCode) {
            return new yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              'accountholder.account.applicant.fiscalCode'
            );
          }

          // search required
          if (!valuesCasted?.searchAccountholder) {
            return new yup.ValidationError(
              'Effettuare ricerca',
              undefined,
              'accountholder.searchAccountholder'
            );
          }

          return true;
        }),
    };
  }
  return yup
    .object()
    .shape(schema)
    .test('', '', (values) => {
      const valuesCasted = values as unknown as HistoricalFormVM;
      const accountHolder = values?.accountholder as AccordionAccountholderFormVM;
      const applicant = values?.applicant as AccordionApplicantFormVM;

      // same as applicant
      if (valuesCasted?.accountholder?.sameApplicant) {
        // if (!valuesCasted?.certifiableApplicant) {
        //   return new yup.ValidationError(
        //     'Soggetto non certificabile',
        //     undefined,
        //     'certifiableApplicant'
        //   );
        // }
      } else {
        if (accountHolder?.account?.applicant?.fiscalCode === applicant?.fiscalCode) {
          return new yup.ValidationError(
            `Richiedente è uguale all'intestatario`,
            undefined,
            'applicant.fiscalCode'
          );
        }
        // if (!valuesCasted?.accountholder?.certifiableAccountholder) {
        //   return new yup.ValidationError(
        //     'Soggetto non certificabile',
        //     undefined,
        //     'certifiableAccountholder'
        //   );
        // }
      }

      if (valuesCasted?.dateForm && valuesCasted?.dateForm?.toString() !== 'Invalid Date' && open) {
        const newDate = DateTime.fromISO((valuesCasted?.dateForm as Date)?.toISOString());
        const diffBtwDates = Math.round(newDate?.diffNow('days')?.days);

        if (diffBtwDates === 0 || diffBtwDates < 0) {
          return true;
        }

        if (diffBtwDates > 0) {
          return new yup.ValidationError(
            'La data di riferimento deve esser uguale o minore alla data odierna',
            undefined,
            'dateForm'
          );
        }
      }

      return true;
    });
};

const HistoricalPage = (): React.ReactElement => {
  const [certificatePdf, getPdfCertificate] = useHttpFetch<
    HttpResponse<ResponseCertificateHistorical>,
    HttpResponse<ResponseCertificateHistorical>
  >(`${__CERTIFICATION_API_URL__}/richiesta-certificato/storico`, {
    onBootstrap: false,
    options: {
      method: 'POST',
    },
    transformer: transformResponseRequestCertificateHistorical,
  });

  const castedCertificatePdf = certificatePdf.data as HttpResponse<ResponseCertificateHistorical>;

  // ModalCertificate
  const [open, setOpen] = useState<boolean>(false);

  // ModalPrintSuccess
  const [modalPrintSuccess, setModalPrintSuccess] = useState<{ open: boolean; total?: number }>({
    open: false,
  });

  // InfoCertification
  const [infoCertification, setInfoCertification] = useState<{ open: boolean; total?: number }>({
    open: false,
  });

  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });

  // Handle the submit
  const handleSubmit = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const { userProfile } = useUserProfile();
  const cashManagement = userProfile?.datiLogin?.profile?.servizio?.gestioneCassa;

  const onRequestCertificate = useEventCallback((values: HistoricalFormVM) => {
    const data = transformRequestHistoricalData(values);

    (
      getPdfCertificate({ data }) as ResponsePromise<
        HttpResponse<ResponseCertificateHistorical>,
        HttpResponse<ResponseCertificateHistorical>
      >
    ).then((response) => {
      const responseCasted = response as HttpResponse<ResponseCertificateHistorical>;
      const pdfCertificate = responseCasted?.payload?.contenutoPdf;

      const { modality } = values;

      const {
        amountRightsSecretariat = 0.0,
        amountRightsCivilState = 0.0,
        amountFixedRights = 0.0,
        amountExpenses = 0.0,
        amountTaxStamp = 0.0,
      } = modality || {};

      const rights =
        amountExpenses +
        amountFixedRights +
        amountRightsCivilState +
        amountRightsSecretariat +
        amountTaxStamp;

      // close ModalCertificate
      setOpen(false);

      if (pdfCertificate) {
        // openPdf(pdfCertificate);
        setPdfModal({ data: pdfCertificate, name: 'Storica', open: true });
      }

      const parsedTotal = values?.certificateInternOnly
        ? Number.parseFloat('0,00')
        : Number.parseFloat(rights?.toString() || '0,00');

      // const total = (values?.payment || 0) + parsedTotal;

      // context.setFieldValue('payment', total);

      //open ModalPrintSuccess
      setModalPrintSuccess({ open: false, total: parsedTotal });
    });
  });

  const onYes = useEventCallback(
    (
      values: CommonRequestCertificateInterface,
      setFieldValue: (
        key: keyof CommonRequestCertificateInterface,
        value: CommonRequestCertificateInterface[keyof CommonRequestCertificateInterface]
      ) => void
    ) => {
      const total = (values?.payment || 0) + (modalPrintSuccess?.total || 0);

      setFieldValue('payment', total);

      // close ModalPrintSuccess
      setModalPrintSuccess({ open: false, total: undefined });

      //open InfoCertification
      setInfoCertification({ open: true, total: modalPrintSuccess.total });
    }
  );

  const validationSchema = useMemo(() => {
    return createSchema(open);
  }, [open]);

  return (
    <Fragment>
      <Breadcrumbs items={HistoricalCrumbs} base="sportello" />
      <div>
        <PmiTitle title="Certificazione Storica" />
      </div>
      <div className="pmi-mt-4">
        <div className="pmi-mt-2">
          <Formik
            validationSchema={validationSchema}
            initialValues={
              {
                applicant: { fiscalCode: undefined },
                accountholder: { sameApplicant: true },
                payment: 0,
                certificateInternOnly: cashManagement !== 'S' ? true : false,
                intendedUse: '0',
                dateForm: DateTime.now(),
                generality: {
                  isForeignDocument: DocumentRadioCertificate.IdentityCard,
                },
              } as HistoricalFormVM
            }
            onSubmit={(values) => onRequestCertificate(values)}
            validateOnChange
            validateOnBlur
            // validateOnMount
          >
            <Form>
              <Grid container spacing={1}>
                <Grid item xs={12} md={3}>
                  <HistoricalPageButtons handleRequestCertificate={handleSubmit} />
                </Grid>
                <Grid item xs={12} md={9}>
                  <div className="pmi-px-md-4">
                    <HistoricalForm />
                  </div>
                </Grid>
              </Grid>
              <ModalCertificate open={open} onClose={setOpen} />
              <ModalPrintSuccess
                open={modalPrintSuccess.open}
                onClose={() => setModalPrintSuccess({ open: false })}
                onYes={onYes}
              />
              <ModalInfoCertification
                open={infoCertification.open}
                onClose={() => setInfoCertification({ open: false })}
                title={castedCertificatePdf?.payload?.numeroIdentificativoDocumento}
                amountCertifate={infoCertification.total?.toFixed(2)}
              />
              <ModalViewerPDF
                open={pdfModal.open}
                data={pdfModal.data}
                name={pdfModal.name}
                title="Certificazione"
                onClose={() => {
                  setPdfModal({ data: '', open: false, name: '' });
                  setModalPrintSuccess((oldState) => ({ ...oldState, open: true }));
                }}
              />
            </Form>
          </Formik>
        </div>
      </div>
    </Fragment>
  );
};

HistoricalPage.displayName = 'Historical page';

export default HistoricalPage;
